import React, { useState } from 'react';

// Export to make it a module
export {};

interface AccessCodeModalProps {
  onSubmit: (accessCode: string) => void;
  onCancel: () => void;
  error?: string | null;
}

const AccessCodeModal: React.FC<AccessCodeModalProps> = ({ onSubmit, onCancel, error }) => {
  const [accessCode, setAccessCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessCode.trim()) return;

    setIsSubmitting(true);
    onSubmit(accessCode);
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-md flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4">
          <h2 className="text-xl font-bold text-white text-center">Access Code Required</h2>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          <div className="mb-6 text-center">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-purple-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              ></path>
            </svg>
            <p className="text-gray-700 mb-4">
              This wheel is protected with an access code. Please enter the code provided by the
              wheel owner.
            </p>
          </div>

          {error && (
            <div
              className="bg-red-50 border-l-4 border-red-400 text-red-700 p-4 mb-6 rounded"
              role="alert"
            >
              <p className="font-medium">Error</p>
              <p className="mt-1">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label htmlFor="accessCode" className="block text-gray-700 font-medium mb-2">
                Access Code
              </label>
              <input
                type="text"
                id="accessCode"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Enter access code"
                value={accessCode}
                onChange={e => setAccessCode(e.target.value)}
                // Focus the input after render without using autoFocus
                ref={input => {
                  if (input) {
                    // Small delay to ensure the modal is fully rendered
                    setTimeout(() => input.focus(), 100);
                  }
                }}
                required
              />
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={onCancel}
                className="py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!accessCode.trim() || isSubmitting}
                className={`py-2 px-4 rounded-md font-medium text-white transition-colors ${
                  accessCode.trim() && !isSubmitting
                    ? 'bg-purple-600 hover:bg-purple-700'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AccessCodeModal;
